import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useSeoExtras } from '../context/SiteSeoContext';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import CategoryBadge from '../components/blog/CategoryBadge';
import SimilarArticles from '../components/blog/SimilarArticles';
import AdContainer from '../components/ads/AdContainer';
import ShareButtons from '../components/blog/ShareButtons';
import BookmarkButton from '../components/blog/BookmarkButton';
import { AuthorByline } from '../components/blog/AuthorSocialLinks';
import { Container } from '../components/ui/Container';
import Spinner from '../components/ui/Spinner';
import SEO from '../components/seo/SEO';
import StructuredData, { generateArticleSchema, generateBreadcrumbSchema, generateFAQPageSchema, generateHowToSchema } from '../components/seo/StructuredData';
import CrawlerSeoContent from '../components/seo/CrawlerSeoContent';
import { accordionFaqsFromContent, extractHowToFromContent, allFaqsFromContent } from '../utils/faqFromContent';
import { buildTocFromContent, stripAccordions } from '../utils/articleHeadings';
import TableOfContents from '../components/blog/TableOfContents';
import ArticleOffers from '../components/blog/ArticleOffers';
import ArticleFaq from '../components/blog/ArticleFaq';
import { fetchArticleBySlug, fetchSimilarArticles } from '../store/actions/articleActions';
import { transformArticle, transformArticles } from '../utils/transformData';
import { resolvePublicArticleRobots } from '../utils/publicArticleSeo';
import {
  setArticleLocaleNavigation,
} from '../utils/articleLocaleNavigation';
import {
  hidePrerenderStaticContent,
} from '../utils/prerenderMeta';
import CommentList from '../components/comments/CommentList';
import CommentForm from '../components/comments/CommentForm';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.mutedForeground};
  text-decoration: none;
  transition: color 0.2s;
  margin-bottom: 2rem;
  font-size: 0.9375rem;
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    margin-bottom: 2.5rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.0625rem;
    margin-bottom: 3rem;
  }
  
  &:hover {
    color: ${props => props.theme.colors.foreground};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    
    @media (min-width: 1024px) {
      width: 1.125rem;
      height: 1.125rem;
    }
    
    @media (min-width: 1440px) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 2rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 380px;
    gap: 3rem;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: 1fr 420px;
    gap: 4rem;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: 1fr 480px;
    gap: 5rem;
  }
`;

const Article = styled.article`
  max-width: none;
`;

const ArticleHeader = styled.header`
  margin-bottom: 2rem;
`;

const ArticleTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  line-height: 1.2;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.foreground};
  
  @media (min-width: 768px) {
    font-size: 2.75rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3.5rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 4rem;
  }
`;

const ArticleExcerpt = styled.p`
  font-size: 1.125rem;
  color: ${props => props.theme.colors.mutedForeground};
  line-height: 1.75;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.375rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.5rem;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
    gap: 1.25rem;
    margin-bottom: 2rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1rem;
    gap: 1.5rem;
    margin-bottom: 2.5rem;
  }
  
  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    svg {
      width: 1rem;
      height: 1rem;
      
      @media (min-width: 1024px) {
        width: 1.125rem;
        height: 1.125rem;
      }
      
      @media (min-width: 1440px) {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeaturedImage = styled.div`
  border-radius: ${props => props.theme.borderRadius['2xl']};
  overflow: hidden;
  margin-bottom: 2rem;
`;

const Image = styled.img`
  width: 100%;
  height: 16rem;
  object-fit: cover;
  
  @media (min-width: 768px) {
    height: 24rem;
  }
`;

const ArticleContent = styled.div`
  p {
    color: ${props => props.theme.colors.foreground}e6;
    line-height: 1.75;
    margin-bottom: 1.5rem;
    font-size: 1rem;
    
    @media (min-width: 768px) {
      font-size: 1.125rem;
    }
  }

  h2, h3, h4 {
    font-family: ${props => props.theme.typography.fonts.display};
    font-weight: ${props => props.theme.typography.fontWeights.semibold};
    color: ${props => props.theme.colors.foreground};
    line-height: 1.3;
    scroll-margin-top: 6rem;
  }

  h2 {
    font-size: 1.5rem;
    margin: 2.5rem 0 1rem;

    @media (min-width: 768px) {
      font-size: 1.875rem;
    }
  }

  h3 {
    font-size: 1.25rem;
    margin: 2rem 0 0.75rem;

    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
  }

  ul, ol {
    margin: 0 0 1.5rem;
    padding-left: 1.5rem;
    color: ${props => props.theme.colors.foreground}e6;
    line-height: 1.75;

    li {
      margin-bottom: 0.5rem;
    }

    @media (min-width: 768px) {
      font-size: 1.125rem;
    }
  }

  blockquote {
    margin: 1.5rem 0;
    padding: 0.5rem 0 0.5rem 1.25rem;
    border-left: 4px solid ${props => props.theme.colors.primary}59;
    color: ${props => props.theme.colors.mutedForeground};
    font-style: italic;
  }

  pre {
    background-color: ${props => props.theme.colors.muted};
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.md};
    padding: 1rem;
    overflow-x: auto;
    margin: 1.5rem 0;
    font-size: 0.875rem;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.lg};
    margin: 1.5rem 0;
    display: block;
  }

  figure {
    margin: 1.5rem 0;

    figcaption {
      margin-top: 0.5rem;
      font-size: 0.875rem;
      color: ${props => props.theme.colors.mutedForeground};
      text-align: center;
    }
  }

  figure.article-video-embed,
  div.article-video-embed,
  div[data-youtube-video] {
    position: relative;
    width: 100%;
    margin: 1.75rem 0;
    border-radius: ${props => props.theme.borderRadius.lg};
    overflow: hidden;
    background: #0a0a0a;
    aspect-ratio: 16 / 9;
  }

  figure.article-video-embed iframe,
  div.article-video-embed iframe,
  div[data-youtube-video] iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }

  /* Bare iframe without a sized wrapper */
  > iframe[src*="youtube.com"],
  > iframe[src*="youtube-nocookie.com"],
  > iframe[src*="player.vimeo.com"] {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    height: auto;
    margin: 1.75rem 0;
    border: 0;
    border-radius: ${props => props.theme.borderRadius.lg};
    background: #0a0a0a;
  }

  details {
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.lg};
    background-color: ${props => props.theme.colors.card};
    margin: 1rem 0;
    overflow: hidden;

    summary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      cursor: pointer;
      list-style: none;
      padding: 1rem 1.25rem;
      font-weight: ${props => props.theme.typography.fontWeights.semibold};
      color: ${props => props.theme.colors.foreground};

      &::-webkit-details-marker {
        display: none;
      }

      &::after {
        content: '+';
        font-size: 1.25rem;
        color: ${props => props.theme.colors.mutedForeground};
      }
    }

    &[open] summary::after {
      content: '\\2212';
    }

    & > *:not(summary) {
      padding: 0 1.25rem 1.25rem;
      color: ${props => props.theme.colors.mutedForeground};
      line-height: 1.7;
    }
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
    word-break: break-word;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
    font-size: 0.9375rem;
    display: block;
    overflow-x: auto;
  }

  th, td {
    border: 1px solid ${props => props.theme.colors.border};
    padding: 0.6rem 0.75rem;
    vertical-align: top;
    text-align: left;
  }

  th {
    background-color: ${props => props.theme.colors.muted};
    font-weight: ${props => props.theme.typography.fontWeights.semibold};
    color: ${props => props.theme.colors.foreground};
  }

  td {
    color: ${props => props.theme.colors.foreground}e6;
  }
`;

const TRUSTED_VIDEO_EMBED_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
]);

const isTrustedVideoEmbedSrc = (src) => {
  try {
    const url = new URL(String(src || ''), 'https://example.com');
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    return TRUSTED_VIDEO_EMBED_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
};

const sanitizeContentHtml = (rawHtml = '') => {
  if (!rawHtml || typeof rawHtml !== 'string') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');

  // Remove dangerous elements (keep trusted video iframes)
  doc.querySelectorAll('script, object, embed, form, style, link, meta').forEach((el) => {
    el.remove();
  });

  doc.querySelectorAll('iframe').forEach((el) => {
    const src = el.getAttribute('src') || '';
    if (!isTrustedVideoEmbedSrc(src)) {
      el.remove();
      return;
    }
    // Keep only safe iframe attributes
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (
        ![
          'src',
          'title',
          'allow',
          'allowfullscreen',
          'frameborder',
          'loading',
          'referrerpolicy',
          'width',
          'height',
          'class',
        ].includes(name)
      ) {
        el.removeAttribute(attr.name);
      }
    });
    if (!el.getAttribute('allow')) {
      el.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
      );
    }
    el.setAttribute('allowfullscreen', 'true');
    el.setAttribute('loading', el.getAttribute('loading') || 'lazy');
    el.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  });

  // Remove dangerous attributes/events and javascript: urls
  doc.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value || '';

      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }

      if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(value)) {
        el.removeAttribute(attr.name);
      }
    });
  });

  // Collapse author-inserted accordions by default for readers
  doc.querySelectorAll('details[open]').forEach((el) => {
    el.removeAttribute('open');
  });

  // Enforce follow links in article bodies (strip nofollow-style rel tokens)
  doc.querySelectorAll('a[rel]').forEach((anchor) => {
    const relValue = (anchor.getAttribute('rel') || '').trim();
    if (!relValue) {
      anchor.removeAttribute('rel');
      return;
    }
    const filtered = relValue
      .split(/\s+/)
      .map((token) => token.trim().toLowerCase())
      .filter((token) => token && token !== 'nofollow' && token !== 'ugc' && token !== 'sponsored');
    if (filtered.length === 0) {
      anchor.removeAttribute('rel');
    } else {
      anchor.setAttribute('rel', filtered.join(' '));
    }
  });

  return doc.body.innerHTML;
};

const ShareSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ShareLabel = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1rem;
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const StickySidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (min-width: 1024px) {
    position: sticky;
    top: 5rem; /* below sticky site header (4rem) */
    max-height: calc(100vh - 5.5rem);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-right: 0.25rem;
    scrollbar-width: thin;
  }
`;

const NotFoundWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const NotFoundContent = styled.main`
  ${Container} {
    padding-top: 4rem;
    padding-bottom: 4rem;
    text-align: center;
  }
`;

const NotFoundTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
`;

const NotFoundText = styled.p`
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 2rem;
`;

const ArticleDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { slug } = useParams();
  const { language, region, availableRegions, apiRegion, getArticleUrl, getCategoryUrl, loading: languageLoading } = useLanguage();
  const hadPrerenderMeta = useRef(
    typeof document !== 'undefined' &&
      !!document.querySelector('meta[name="prerender-status"][content="ok"]')
  );
  
  const [commentRefresh, setCommentRefresh] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const {
    article: articleFromStore,
    similarArticles: similarFromStore,
    articleLoading,
    articleError,
    articleErrorNotFound,
    articleFetchSlug,
  } = useSelector((state) => state.article);

  // Use language from context (set from URL path synchronously via LanguageContext).
  // Do NOT prefer a stale Redux article.language for the fetch — that caused wrong-locale
  // first requests and soft-404 noindex for Googlebot on regional URLs.
  const activeLanguage = language;
  const contentLanguage = String(activeLanguage || 'en').toLowerCase();
  const contentRegion = String(apiRegion || region?.code || 'US').toUpperCase();

  useEffect(() => {
    if (slug && !languageLoading) {
      dispatch(fetchArticleBySlug(slug, contentLanguage, contentRegion));
    }
  }, [dispatch, slug, contentLanguage, contentRegion, languageLoading]);

  useEffect(() => {
    const raw = articleFromStore?.data || articleFromStore;
    if (raw?.availableRegions) {
      setArticleLocaleNavigation(raw);
    }
  }, [articleFromStore]);

  const rawArticle = articleFromStore?.data || articleFromStore || null;

  // Redux can still hold the previous article for one render after the URL
  // changes (useEffect fetch has not dispatched yet). Never expose that
  // article's SEO/body as the current route.
  const articleMatchesRoute = useMemo(() => {
    if (!rawArticle || !slug) return false;
    const known = new Set();
    if (rawArticle.slug) known.add(rawArticle.slug);
    if (rawArticle.baseSlug) known.add(rawArticle.baseSlug);
    Object.values(rawArticle.availableRegions || {}).forEach((info) => {
      if (info?.slug) known.add(info.slug);
    });
    Object.values(rawArticle.availableTranslations || {}).forEach((tr) => {
      if (tr?.slug) known.add(tr.slug);
    });
    return known.has(slug);
  }, [rawArticle, slug]);

  // Transform article data - handle multilingual response
  const article = rawArticle && articleMatchesRoute ? transformArticle(rawArticle) : null;

  const availableTranslations = article
    ? (rawArticle?.availableTranslations || {})
    : {};
  const articleRegionSlugs = article
    ? (rawArticle?.availableRegions || {})
    : {};

  // Do NOT client-navigate between regional/locale slugs. Googlebot treats
  // React navigate() as a redirect; races (wrong region on first fetch) caused
  // GSC "Redirect error" even when the final URL is HTTP 200. Keep the crawled
  // URL stable and rely on canonical + hreflang instead.

  const excludeArticleSlugs = useMemo(() => {
    const slugs = new Set([slug, article?.slug].filter(Boolean));
    Object.values(articleRegionSlugs).forEach((info) => {
      if (info?.slug) slugs.add(info.slug);
    });
    Object.values(availableTranslations).forEach((tr) => {
      if (tr?.slug) slugs.add(tr.slug);
    });
    return slugs;
  }, [slug, article?.slug, articleRegionSlugs, availableTranslations]);

  useEffect(() => {
    if (!article?._id) return;
    dispatch(
      fetchSimilarArticles({
        articleId: article._id,
        limit: 3,
        lang: contentLanguage,
        region: contentRegion,
      })
    );
  }, [dispatch, article?._id, contentLanguage, contentRegion]);

  const similarArticles = useMemo(() => {
    const currentId = article?._id;
    const seen = new Set();
    return transformArticles(similarFromStore)
      .filter((a) => {
        if (!a) return false;
        if (currentId && a._id === currentId) return false;
        if (excludeArticleSlugs.has(a.slug)) return false;
        if (seen.has(a._id || a.slug)) return false;
        seen.add(a._id || a.slug);
        return true;
      })
      .slice(0, 3);
  }, [similarFromStore, article?._id, excludeArticleSlugs]);

  const seoExtras = useSeoExtras();

  const alternateUrls = useMemo(() => {
    const urls = {};
    const includeRegionalVariants = seoExtras.includeRegionalVariants !== false;
    const regionEntries = Object.entries(articleRegionSlugs);

    if (regionEntries.length > 0) {
      regionEntries.forEach(([code, info]) => {
        if (!info?.slug) return;
        const regionCode = String(code).toLowerCase();
        const hreflang = includeRegionalVariants
          ? `${info.language || 'en'}-${String(code).toUpperCase()}`.toLowerCase()
          : (info.language || 'en').toLowerCase();
        urls[hreflang] = regionCode === 'us'
          ? `/article/${info.slug}`
          : `/${regionCode}/article/${info.slug}`;
      });
      return urls;
    }

    const translationKeys = Object.keys(availableTranslations);
    if (translationKeys.length < 2) return {};

    translationKeys.forEach((lang) => {
      const translationSlug = availableTranslations[lang]?.slug || slug;
      const primaryRegion = availableRegions.find(
        (r) => r.defaultLanguage?.toLowerCase() === lang.toLowerCase()
      );
      if (primaryRegion) {
        const regionCode = primaryRegion.code.toLowerCase();
        const hreflang = includeRegionalVariants
          ? `${lang}-${primaryRegion.code}`.toLowerCase()
          : lang.toLowerCase();
        urls[hreflang] = regionCode === 'us'
          ? `/article/${translationSlug}`
          : `/${regionCode}/article/${translationSlug}`;
      } else {
        urls[lang] = getArticleUrl(translationSlug);
      }
    });
    return urls;
  }, [articleRegionSlugs, availableTranslations, availableRegions, slug, getArticleUrl, seoExtras.includeRegionalVariants]);

  // Prefer the URL slug so meta/canonical match the crawled path (no JS redirect).
  const articleUrl = getArticleUrl(slug || article?.slug);
  const accordionFaqs = useMemo(
    () => (article?.content ? accordionFaqsFromContent(article.content) : []),
    [article?.content]
  );
  const { items: tocItems, html: contentHtmlWithIds } = useMemo(
    () => buildTocFromContent(article?.content),
    [article?.content]
  );
  // Authored accordions are surfaced in the dedicated FAQ section, so strip
  // them from the inline body to avoid duplicate Q/A on the page.
  const contentForRender = useMemo(
    () => stripAccordions(contentHtmlWithIds),
    [contentHtmlWithIds]
  );
  const showToc = tocItems.length >= 3;

  useEffect(() => {
    if (!contentHtmlWithIds || typeof window === 'undefined') return undefined;
    const hash = window.location.hash?.slice(1);
    if (!hash) return undefined;
    const raf = window.requestAnimationFrame(() => {
      const el = document.getElementById(decodeURIComponent(hash));
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [contentHtmlWithIds]);
  const schemaFaqs = useMemo(
    () => (article?.content ? allFaqsFromContent(article.content) : []),
    [article?.content]
  );
  const faqSchema = useMemo(
    () => (schemaFaqs.length >= 2 ? generateFAQPageSchema(schemaFaqs, articleUrl) : null),
    [schemaFaqs, articleUrl]
  );
  const howToData = useMemo(
    () => (article?.content ? extractHowToFromContent(article.content) : null),
    [article?.content]
  );
  const howToSchema = useMemo(
    () => (howToData ? generateHowToSchema(howToData, articleUrl) : null),
    [howToData, articleUrl]
  );

  useEffect(() => {
    if (article) {
      hidePrerenderStaticContent();
    }
  }, [article]);

  // Soft-404 / noindex ONLY for confirmed 404/403 from the slug fetch.
  // Transient API failures keep showing the spinner (no robots tag) so Googlebot
  // never snapshots noindex on a live article URL.
  const fetchMatchesRoute = !articleFetchSlug || articleFetchSlug === slug;
  const pendingFetch =
    Boolean(slug) &&
    !article &&
    (languageLoading ||
      articleLoading ||
      !fetchMatchesRoute ||
      (!articleError && !articleMatchesRoute) ||
      (articleError && !articleErrorNotFound));

  // Prefer authored SEO; fall back to title/excerpt so Google never sees bare "Bloomwik".
  const seoTitle = (article?.seo?.metaTitle || article?.title || '').trim();
  const seoDescription = (article?.seo?.metaDescription || article?.excerpt || '').trim();

  if (languageLoading || articleLoading || pendingFetch) {
    // Keep prerender <head> + static article HTML until client SEO mounts.
    if (hadPrerenderMeta.current) {
      return null;
    }
    // No Header/Footer here: Google sometimes snapshots the loading shell and
    // uses nav ("Skip to main content…") as the meta description.
    return (
      <PageWrapper>
        <Main>
          <Container>
            <Spinner />
          </Container>
        </Main>
      </PageWrapper>
    );
  }

  if (!article) {
    if (hadPrerenderMeta.current) {
      return null;
    }
    return (
      <NotFoundWrapper>
        <SEO
          noindex
          strict
          title="Article Not Found"
          description="The article you're looking for doesn't exist."
        />
        <Header />
        <NotFoundContent>
          <Container>
            <NotFoundTitle>Article Not Found</NotFoundTitle>
            <NotFoundText>
              {articleError || "The article you're looking for doesn't exist."}
            </NotFoundText>
            <BackLink to="/">
              <ArrowLeft />
              Back to Home
            </BackLink>
          </Container>
        </NotFoundContent>
        <Footer />
      </NotFoundWrapper>
    );
  }

  const articleRobots = resolvePublicArticleRobots(article?.seo?.robots);

  return (
    <PageWrapper>
      <SEO
        key={`article-${slug}`}
        strict
        title={seoTitle}
        description={seoDescription}
        ogTitle={article?.seo?.ogTitle || seoTitle}
        ogDescription={article?.seo?.ogDescription || seoDescription}
        twitterTitle={article?.seo?.twitterTitle || seoTitle}
        twitterDescription={article?.seo?.twitterDescription || seoDescription}
        image={article?.seo?.ogImage || article?.imageUrl}
        url={articleUrl}
        canonicalUrl={article?.seo?.canonicalUrl || articleUrl}
        robots={articleRobots}
        type="article"
        author={article?.author}
        publishedTime={article?.publishedAt}
        modifiedTime={article?.updatedAt}
        articleAuthor={article?.author}
        tags={article?.tags || []}
        language={activeLanguage}
        availableLanguages={availableTranslations}
        alternateUrls={alternateUrls}
        twitterCard={article?.twitterCard}
        {...seoExtras}
        keywords={[
          ...(article?.seo?.keywords || []),
          article?.seo?.focusKeyword,
        ].filter(Boolean)}
      />
      <StructuredData
        data={generateArticleSchema(
          {
            ...article,
            slug: article?.slug || slug,
            date: article?.publishedAt || article?.date,
            updatedAt: articleFromStore?.data?.updatedAt || articleFromStore?.updatedAt,
            publishedAt: article?.publishedAt || articleFromStore?.data?.publishedAt || articleFromStore?.publishedAt,
          },
          activeLanguage,
          articleUrl
        )}
      />
      {faqSchema && <StructuredData data={faqSchema} />}
      {howToSchema && <StructuredData data={howToSchema} />}
      {article && (
        <StructuredData
          data={generateBreadcrumbSchema([
            { name: 'Home', url: activeLanguage === 'en' ? '/' : `/${activeLanguage}` },
            ...(article.category ? [
              { name: 'Categories', url: activeLanguage === 'en' ? '/categories' : `/${activeLanguage}/categories` },
              { 
                name: article.category, 
                url: getCategoryUrl(
                  article.category?.toLowerCase().replace(/\s+/g, '-'), 
                  activeLanguage
                )
              }
            ] : []),
            { name: article.title, url: articleUrl }
          ])}
        />
      )}
      <CrawlerSeoContent
        title={article.title}
        intro={article.excerpt}
        author={article.author}
        category={article.category}
        bodyParagraphs={Array.isArray(article.content) ? article.content : [article.content]}
        faqs={schemaFaqs}
      />
      <Header />
      
      <Main>
        <Container>
          <BackLink to={activeLanguage === 'en' ? '/' : `/${activeLanguage}`}>
            <ArrowLeft />
            Back to Articles
          </BackLink>

          <ContentGrid>
            <Article>
              <ArticleHeader>
                <CategoryBadge
                  category={article.category}
                  color={article.categoryColor}
                  imageUrl={article.categoryImageUrl}
                  slug={article.categorySlug}
                />
                
                <ArticleTitle>{article.title}</ArticleTitle>

                <ArticleExcerpt>{article.excerpt}</ArticleExcerpt>

                <MetaInfo>
                  <div>
                    <Calendar />
                    <span>{article.date}</span>
                  </div>
                  {article.updatedAt &&
                    article.publishedAt &&
                    new Date(article.updatedAt).getTime() > new Date(article.publishedAt).getTime() + 60000 && (
                    <div>
                      <Clock />
                      <span>
                        Updated{' '}
                        {new Date(article.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                  <div>
                    <Clock />
                    <span>{article.readTime}</span>
                  </div>
                </MetaInfo>

                <AuthorSection>
                  <AuthorInfo>
                    <AuthorByline
                      name={article.author}
                      slug={article.authorSlug}
                      avatar={article.authorAvatar}
                    />
                  </AuthorInfo>
                  <ActionButtons>
                    <ShareButtons
                      title={article.title}
                      url={articleUrl}
                      description={article.excerpt}
                    />
                    <BookmarkButton article={{ ...article, slug }} />
                  </ActionButtons>
                </AuthorSection>
              </ArticleHeader>

              {showToc && <TableOfContents items={tocItems} />}

              <FeaturedImage>
                <Image src={article.imageUrl} alt={article.imageAlt?.trim() || article.title} />
              </FeaturedImage>

              <ArticleContent
                dangerouslySetInnerHTML={{ __html: sanitizeContentHtml(contentForRender) }}
              />

              <ArticleFaq faqs={accordionFaqs} />

              {article._id && (
                <>
                  <CommentList
                    articleId={article._id}
                    refreshKey={commentRefresh}
                    commentCount={article.commentCount}
                    onReply={(comment) => {
                      setReplyTo(comment);
                      document.getElementById('comment-form')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  />
                  <CommentForm
                    articleId={article._id}
                    replyTo={replyTo}
                    onCancelReply={() => setReplyTo(null)}
                    onSubmitted={() => setCommentRefresh((k) => k + 1)}
                  />
                </>
              )}

              <ShareSection>
                <ShareRow>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ShareLabel>{t('article.shareThisArticle')}</ShareLabel>
                    <ShareButtons
                      title={article.title}
                      url={articleUrl}
                      description={article.excerpt}
                    />
                  </div>
                  <BookmarkButton article={{ ...article, slug }} />
                </ShareRow>
              </ShareSection>
            </Article>

            <Sidebar>
              <StickySidebar>
                <ArticleOffers offers={article?.offers} articleId={article?._id} language={contentLanguage} />

                {similarArticles.length > 0 && (
                  <SimilarArticles articles={similarArticles} />
                )}

                <AdContainer
                  placement="inline"
                  limit={1}
                />

                <div>
                  <AdContainer
                    placement="article_sidebar"
                    limit={1}
                  />
                </div>
              </StickySidebar>
            </Sidebar>
          </ContentGrid>
        </Container>
      </Main>

      <Footer />
    </PageWrapper>
  );
};

export default ArticleDetail;

